// =============================================================================
// UTILITAIRES DE DISPONIBILITÉ - Gestion des demi-journées (AM/PM) et fin de journée (17h)
// =============================================================================

import { JourDetail } from '@/types/calendar';

// Type pour la période de travail actuelle
export type WorkPeriod = 'AM' | 'PM' | 'OFF';

/**
 * Retourne la période de travail actuelle
 * - AM : Matin (avant 13h)
 * - PM : Après-midi (13h-17h)
 * - OFF : Hors travail (après 17h)
 */
export function getCurrentWorkPeriod(): WorkPeriod {
  const hour = new Date().getHours();
  if (hour < 13) return 'AM';      // Matin : 8h-13h
  if (hour < 17) return 'PM';      // Après-midi : 13h-17h
  return 'OFF';                     // Hors travail : après 17h
}

/**
 * Retourne la période actuelle (matin ou après-midi)
 * Matin = avant 13h, Après-midi = 13h et après
 * @deprecated Utiliser getCurrentWorkPeriod() pour gérer aussi la fin de journée (17h)
 */
export function getCurrentPeriod(): 'AM' | 'PM' {
  const hour = new Date().getHours();
  return hour < 13 ? 'AM' : 'PM';
}

/**
 * Vérifie si un membre est affecté pour la période actuelle
 * basé sur jours_details (AM/PM/FULL)
 *
 * Logique :
 * - Avant 13h (AM) : affectations AM et FULL actives
 * - Entre 13h et 17h (PM) : affectations PM et FULL actives
 * - Après 17h (OFF) : AUCUNE affectation active (journée terminée)
 *
 * @param joursDetails - Détails des jours travaillés (peut être null/undefined)
 * @param today - Date du jour au format YYYY-MM-DD
 * @returns true si le membre est affecté pour la période actuelle
 */
export function isAffectedForCurrentPeriod(
  joursDetails: JourDetail[] | null | undefined,
  today: string
): boolean {
  const workPeriod = getCurrentWorkPeriod();

  // Après 17h, personne n'est affecté pour aujourd'hui (journée de travail terminée)
  if (workPeriod === 'OFF') {
    return false;
  }

  // Si pas de jours_details → comportement legacy (journée complète, actif si avant 17h)
  if (!joursDetails || joursDetails.length === 0) {
    return true;
  }

  // Chercher les entrées pour aujourd'hui
  const todayDetails = joursDetails.filter(jd => jd.date === today);

  // Si pas d'entrée pour aujourd'hui dans jours_details → pas affecté
  if (todayDetails.length === 0) {
    return false;
  }

  // Vérifier si une entrée couvre la période actuelle
  return todayDetails.some(jd => {
    if (jd.period === 'FULL') return true; // FULL couvre AM et PM
    if (jd.period === 'AM' && workPeriod === 'AM') return true;
    if (jd.period === 'PM' && workPeriod === 'PM') return true;
    return false;
  });
}

/**
 * Vérifie si un membre est affecté pour la page Équipes (approche hybride)
 *
 * - Pendant les heures ouvrées (8h-17h) : précision AM/PM (comme isAffectedForCurrentPeriod)
 * - En dehors des heures ouvrées (avant 8h / après 17h) : vue jour calendaire
 *   → si le membre a une affectation qui couvre la date → true
 *
 * Permet d'afficher les affectations du jour sur la page Équipes même après 17h,
 * tout en conservant la granularité AM/PM pendant les heures de travail.
 */
export function isAffectedForTeamsPage(
  joursDetails: JourDetail[] | null | undefined,
  today: string
): boolean {
  const workPeriod = getCurrentWorkPeriod();

  // Hors heures ouvrées : vue jour calendaire (si affecté aujourd'hui → true)
  if (workPeriod === 'OFF') {
    if (!joursDetails || joursDetails.length === 0) return true;
    return joursDetails.some(jd => jd.date === today);
  }

  // Pendant les heures ouvrées : précision AM/PM (logique identique à isAffectedForCurrentPeriod)
  if (!joursDetails || joursDetails.length === 0) return true;

  const todayDetails = joursDetails.filter(jd => jd.date === today);
  if (todayDetails.length === 0) return false;

  return todayDetails.some(jd => {
    if (jd.period === 'FULL') return true;
    if (jd.period === 'AM' && workPeriod === 'AM') return true;
    if (jd.period === 'PM' && workPeriod === 'PM') return true;
    return false;
  });
}

/**
 * Retourne le texte de la période actuelle en français
 */
export function getCurrentPeriodLabel(): string {
  return getCurrentPeriod() === 'AM' ? 'ce matin' : 'cet après-midi';
}

/**
 * Retourne le libellé complet de la période
 */
export function getPeriodLabel(period: 'AM' | 'PM' | 'FULL'): string {
  switch (period) {
    case 'AM':
      return 'Matin';
    case 'PM':
      return 'Après-midi';
    case 'FULL':
      return 'Journée';
  }
}

// =============================================================================
// HEURES DE TRAVAIL - Calcul de progression
// =============================================================================

/**
 * Constantes des heures de travail BTP
 * Utilisées pour calculer la progression de la barre temporelle des chantiers
 */
export const WORK_HOURS = {
  START: 8,          // Début de journée à 8h
  HALF_DAY_END: 13,  // Fin matin à 13h (cohérent avec getCurrentPeriod)
  FULL_DAY_END: 17,  // Fin journée complète à 17h
} as const;

/**
 * Détermine l'heure de fin de travail pour un jour donné basé sur jours_details
 * @param joursDetails - Les détails des jours travaillés
 * @param dateStr - La date au format YYYY-MM-DD
 * @returns L'heure de fin (13 pour demi-journée matin, 17 pour journée complète ou demi-journée après-midi)
 */
export function getWorkEndHourForDate(
  joursDetails: JourDetail[] | null | undefined,
  dateStr: string
): number {
  if (!joursDetails || joursDetails.length === 0) {
    // Pas de détails → journée complète par défaut
    return WORK_HOURS.FULL_DAY_END;
  }

  // Chercher l'entrée pour ce jour
  const dayDetail = joursDetails.find(jd => jd.date === dateStr);

  if (!dayDetail) {
    // Pas d'entrée pour ce jour → journée complète par défaut
    return WORK_HOURS.FULL_DAY_END;
  }

  // Déterminer l'heure de fin selon la période
  switch (dayDetail.period) {
    case 'AM':
      // Demi-journée matin → fin à 13h
      return WORK_HOURS.HALF_DAY_END;
    case 'PM':
      // Demi-journée après-midi → fin à 17h (mais début à 13h)
      return WORK_HOURS.FULL_DAY_END;
    case 'FULL':
    default:
      // Journée complète → fin à 17h
      return WORK_HOURS.FULL_DAY_END;
  }
}

/**
 * Détermine l'heure de début de travail pour un jour donné basé sur jours_details
 * @param joursDetails - Les détails des jours travaillés
 * @param dateStr - La date au format YYYY-MM-DD
 * @returns L'heure de début (8 pour journée complète ou matin, 13 pour après-midi seulement)
 */
export function getWorkStartHourForDate(
  joursDetails: JourDetail[] | null | undefined,
  dateStr: string
): number {
  if (!joursDetails || joursDetails.length === 0) {
    return WORK_HOURS.START;
  }

  const dayDetail = joursDetails.find(jd => jd.date === dateStr);

  if (!dayDetail) {
    return WORK_HOURS.START;
  }

  // Si seulement PM, le travail commence à 13h
  return dayDetail.period === 'PM' ? WORK_HOURS.HALF_DAY_END : WORK_HOURS.START;
}
